<template>
  <SubmenuMainContainer v-model:currentMenu="currentMenu" :menus="menus" :dataPlan="dataPlan">
    <template #[SectionKey.Tags]>
      <PlatformSettingsTemplate />
    </template>

    <template #[SectionKey.QuickAnswers]>
      <QuickAnswersTemplate />
    </template>

    <template #[SectionKey.SendingSettings]>
      <SendingSettingsTemplate
        v-if="customerData"
        v-model:enable_enter="customerData.enable_enter"
      />
    </template>

    <template #[SectionKey.IncomingSound]>
      <SoundIncomingTemplate
        v-if="customerData"
        v-model:enable_sound_message="customerData.enable_sound_message"
      />
    </template>

    <template #[SectionKey.ApiIntegration]>
      <ApiKeyTemplate
        v-if="customerData"
        :token="customerData.api_token ?? ''"
        :dataPlan="dataPlan"
      />
    </template>

    <template #[SectionKey.McpIntegration]>
      <McpIntegrationTemplate
        v-if="customerData"
        serverUrl="mcp-connector.beexcontact.com"
        :token="customerData.cloud_api_key ?? ''"
        :tokenExpiresAt="customerData.token_expiration ?? ''"
        :dataPlan="dataPlan"
      />
    </template>

    <template #[SectionKey.CallbackIntegration]>
      <CallbackConfig
        v-if="customerData"
        :dataPlan="dataPlan"
        :callbackUrl="customerData.api_url_callback ?? ''"
        :token="customerData.token_url_callback"
        :disabled="customerData.status_callback === CallbackEventStatus.Inactive"
      />
    </template>

    <template #[SectionKey.BlackList]>
      <BlackListTemplate />
    </template>

    <template #[SectionKey.UnSubscriber]>
      <UnsubscriberTemplate />
    </template>
  </SubmenuMainContainer>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, provide, watch } from "vue";
import { SoundOutlined, ThunderboltOutlined } from "@ant-design/icons-vue";

import { SubmenuData } from "@/@types/shared/pickers";
import { Customer } from "@/@types/global";
import { fetchLoggedBusiness } from "@/app/settings/services";
import { apiService } from "@/app/platformSettings/services/ApiService";
import { CallbackEventStatus } from "@/@types/platformSettings/callbacks";

import SubmenuMainContainer from "@/app/shared/components/organisms/SubmenuMainContainer.vue";
import PlatformSettingsTemplate from "@/app/platformSettings/components/template/PlatformSettingsTemplate.vue";
import QuickAnswersTemplate from "@/app/platformSettings/components/template/QuickAnswersTemplate.vue";
import SendingSettingsTemplate from "@/app/platformSettings/components/template/SendingSettingsTemplate.vue";
import ApiKeyTemplate from "@/app/platformSettings/components/template/ApiKeyTemplate.vue";
import McpIntegrationTemplate from "@/app/platformSettings/components/template/McpIntegrationTemplate.vue";
import CallbackConfig from "@/app/platformSettings/components/template/CallbackConfig.vue";
import BlackListTemplate from "@/app/platformSettings/components/template/BlackListTemplate.vue";
import UnsubscriberTemplate from "@/app/platformSettings/components/template/UnsubscriberTemplate.vue";
import { BlackListTabText, BlackListTabIcon, BlackListTab } from "@/app/platformSettings/utils/enums";

import tagsImage from "@/app/platformSettings/assets/tags.svg";
import soundImage from "@/app/platformSettings/assets/sound.svg";
import keyImage from "@/app/settingsMenu/assets/key.svg";
import mcpImage from "@/app/settingsMenu/assets/mcp.svg";
import codeImage from "@/app/platformSettings/assets/code.svg";
// import blockImage from "@/app/platformSettings/assets/block.svg";
import emailImage from "@/app/platformSettings/assets/email.svg";
import { useStore } from "vuex";
import SoundIncomingTemplate from "../components/template/SoundIncomingTemplate.vue";

enum MenuKey {
  Workspace = "workspace",
  ApiConfig = "api-config",
  UnSubscriber = "un-subscriber",
  BlackList = "black-list",
}

enum SectionKey {
  Tags = "tags",
  QuickAnswers = "quick-answers",
  SendingSettings = "sending-settings",
  ApiIntegration = "api-integration",
  McpIntegration = "mcp-integration",
  CallbackIntegration = "callback-integration",
  UnSubscriber = "un-subscriber",
  BlackList = "black-list",
  IncomingSound = "incoming-sound", // mensaje entrante
}

/**** validations plan confing ****/
const store = useStore();
const dataPlan = computed(() => store.getters["getDataPlanDetails"]);

const currentMenu = ref(MenuKey.Workspace);
const customerData = ref<Customer>();

const fetchCustomer = async () => {
  customerData.value = await fetchLoggedBusiness();
};

const currentTabBlacklist = ref<number>(1);
provide("currentTabBlackList", currentTabBlacklist);

onMounted(async () => {
  console.log('onMounted');
  fetchCustomer();
});

const menus = computed<SubmenuData<MenuKey, SectionKey>[]>(() => [
  {
    name: "Workspace",
    key: MenuKey.Workspace,
    sections: [
      {
        title: "Etiquetas",
        key: SectionKey.Tags,
        icon: tagsImage,
      },
      {
        title: "Respuestas rápidas",
        key: SectionKey.QuickAnswers,
        icon: ThunderboltOutlined,
      },
      {
        title: "Configuración de envío de mensajes",
        key: SectionKey.SendingSettings,
        icon: ThunderboltOutlined,
      },
      {
        title: "Configuración de sonido para tickets activos",
        key: SectionKey.IncomingSound,
        icon: soundImage,
      },
    ],
  },
  {
    name: "API Key, MCP y Callback",
    key: MenuKey.ApiConfig,
    sections: [
      {
        title: "Integración de API Key",
        key: SectionKey.ApiIntegration,
        icon: keyImage,
        planRequired: "beex_api",
      },
      {
        title: "Nueva exposición de APIs v2 y MCP",
        key: SectionKey.McpIntegration,
        icon: mcpImage,
        planRequired: "mcp_token",
      },
      {
        title: "Integración de Callbacks",
        key: SectionKey.CallbackIntegration,
        icon: codeImage,
        isActive: customerData.value?.status_callback === CallbackEventStatus.Active,
        planRequired: 'beex_api',
        onActiveChange: async (active) => {
          console.log("onActiveChange", active);
          await apiService.updateCallbackIntegration(
            active ? CallbackEventStatus.Active : CallbackEventStatus.Inactive
          );
          await fetchCustomer();
        },
      },
    ],
  },
  {
    name: "Lista Negra",
    key: MenuKey.BlackList,
    sections: [
      {
        title: BlackListTabText[currentTabBlacklist.value as BlackListTab],
        key: SectionKey.BlackList,
        // icon: require("@/app/platformSettings/assets/sms.svg"),
        icon: new URL(`../assets/${BlackListTabIcon[currentTabBlacklist.value as BlackListTab]}`, import.meta.url)
          .href,
      },
    ],
  },
  {
    name: "Desuscriptores",
    key: MenuKey.UnSubscriber,
    sections: [
      // {
      //   title: "Lista negra",
      //   key: SectionKey.BlackList,
      //   icon: blockImage,
      // },
      {
        title: "Desuscriptores",
        key: SectionKey.UnSubscriber,
        icon: emailImage,
      },
    ],
  },
]);

onMounted(async () => {
  customerData.value = await fetchLoggedBusiness();
});
</script>

<style lang="sass" scoped>
.page
  display: flex
  height: 100%

.menu
  margin: 12px 0 !important
  padding-top: 10px !important
  border: 1px solid $gray-5 !important
  border-left: none !important
  border-radius: 0 10px 10px 0
  flex: 0 0 220px

.main
  flex: 1 1 auto
  padding: 20px

  .title
    font-size: 20px
    font-weight: 600
    line-height: 20px
    margin: 0

.divider
  margin: 20px 0
  background-color: $gray-4
</style>
